import type { ChangeEventHandler } from 'react';
import { useCallback, useMemo } from 'react';
import { format } from 'date-fns/format';
import { useSearchParams } from 'next/navigation';
import type { FormSettings } from '@mwl/core-lib';
import {
  ageValidator,
  formatPhoneNumber,
  formatResponseErrors,
  fullRegistration,
  isEmail,
  isNotEmpty,
  loginSuccess,
  minLengthValidator,
  minPasswordLength,
  onlyNumbers,
  registerSuccess,
  removeAllSpaces,
  requiredStringValidator,
  requiredValidator,
  selectUserLocale,
  setRecoveryLogin,
  useForm,
  useQueryPopupOpen,
  useSubmitFormAuth,
} from '@mwl/core-lib';

import { useRegister } from '@/context/register';
import { useTypedDispatch, useTypedSelector } from '@/hooks';

import type {
  FormRegistrationExtendedValues,
  UseRegistrationExtendedHandlersParams,
} from './FormRegistrationExtended.types';

function useRegistrationExtendedFormSettings() {
  const searchParams = useSearchParams();
  const promocode = searchParams.get('promocode');

  return useMemo<FormSettings<FormRegistrationExtendedValues>>(
    () => ({
      name: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: '',
      },
      address: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: '',
      },
      dateOfBirth: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
          date: { validate: ageValidator, message: 'error' },
        },
        value: '',
      },
      code: {
        rules: {
          required: { validate: isNotEmpty, message: 'error' },
        },
        value: { format: '', label: '', value: '', prefix: '' },
      },
      country: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: { label: '', value: '', prefix: '' },
      },
      currency: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: { label: '', value: '' },
      },
      email: {
        rules: {
          required: { validate: requiredStringValidator, message: 'error' },
          extended: { validate: isEmail, message: 'error' },
        },
        value: '',
      },
      phone: {
        rules: {
          required: {
            validate: (value) => requiredStringValidator(onlyNumbers(value)),
            message: 'error.requiredField_common',
          },
          minLength: {
            validate: (value, state) =>
              minLengthValidator(removeAllSpaces(state?.code.format || '').length)(onlyNumbers(value)),
            message: 'error',
          },
        },
        value: '',
      },
      password: {
        rules: {
          required: { validate: requiredStringValidator, message: 'error' },
          minLength: { validate: minLengthValidator(minPasswordLength), message: 'error' },
        },
        value: '',
      },
      promoCode: {
        value: promocode || '',
      },
      agree1: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: false,
      },
      agree2: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: false,
      },
      agree3: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: false,
      },
      agree4: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: false,
      },
      agree5: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: false,
      },
      agree6: {
        rules: {
          required: { validate: requiredValidator, message: 'error' },
        },
        value: false,
      },
    }),
    [promocode],
  );
}

export function useRegistrationExtendedForm() {
  const formSettings = useRegistrationExtendedFormSettings();
  const { formState, changeField, handleSubmit, setErrors } = useForm<FormRegistrationExtendedValues>(formSettings);

  const dispatch = useTypedDispatch();

  const locale = useTypedSelector(selectUserLocale);

  const { selectedBonus, onSuccessRegister } = useRegister();

  const { onOpenPopup } = useQueryPopupOpen();

  const {
    state: { error, isLoading },
    onSubmitConfirm,
  } = useSubmitFormAuth({
    requestHandler: fullRegistration,
    formState: formState.values,
    onSuccess: (data) => {
      if (data?.token) {
        dispatch(registerSuccess());
        dispatch(loginSuccess({ token: data.token, skipAuthReload: true }));
        onSuccessRegister();
      }
    },
    onError: (formError) => {
      const isEmailAlreadyExist = !!formError.details?.find((detail) => detail.key === 'error.emailAlreadyExists');
      const isPhoneAlreadyExist = !!formError.details?.find((detail) => detail.key === 'error.phoneAlreadyExists');

      if (isEmailAlreadyExist) {
        dispatch(setRecoveryLogin(formState.values.email));
        onOpenPopup({ queryName: 'reset_password' });
      } else if (isPhoneAlreadyExist) {
        dispatch(setRecoveryLogin(formatPhoneNumber(formState.values.code.value, formState.values.phone)));
        onOpenPopup({ queryName: 'reset_password' });
      } else {
        setErrors(formatResponseErrors<typeof formState.errors>(formError));
      }
    },
  });

  const submitConfirm = async (values: FormRegistrationExtendedValues) => {
    const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || '';

    onSubmitConfirm({
      phonePrefix: values.code.value,
      phone: formatPhoneNumber(values.code.value, values.phone),
      firstName: values.name,
      address: values.address,
      dateOfBirth: values.dateOfBirth,
      countryAlpha2IsoCode: values.country.value,
      currencyIsoCode: values.currency.label,
      email: values.email,
      password: values.password,
      bonusType: selectedBonus.type !== 'REFUSAL' ? selectedBonus.type : undefined,
      promoCode: values.promoCode || undefined,
      timezone,
      locale,
    });
  };

  return {
    changeField,
    handleSubmit: handleSubmit(submitConfirm),
    isLoading,
    error,
    onSubmitConfirm,
    formState,
  };
}

export function useRegistrationExtendedHandlers({ changeField }: UseRegistrationExtendedHandlersParams) {
  const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const { name, value, checked, type, dataset } = event.target;

      const typedName = name as keyof FormRegistrationExtendedValues;

      const removeAllSpacesFn = dataset?.preserveSpace ? (val: string) => val : removeAllSpaces;

      const typedValue = (
        type === 'checkbox' ? checked : removeAllSpacesFn(value)
      ) as ValueOf<FormRegistrationExtendedValues>;

      changeField(typedName, typedValue);
    },
    [changeField],
  );

  const handleChangeDate = useCallback(
    (time: number) => {
      changeField('dateOfBirth', format(new Date(time), 'yyyy-MM-dd'));
    },
    [changeField],
  );

  return {
    handleInputChange,
    handleChangeDate,
  };
}
